<template>
  <div :class="`${tablesCached ? 'd-flex' : 'd-none'} flex-column h-100 w-100 pb-1`">
    <div class="d-flex justify-content-between">
      <div>
        <bulk-reassign v-if="!isSimulation" :inspection-ids-to-assign="selectedQueriedInspectionIds"/>
      </div>
      <div class="d-flex align-items-center mb-3">
        <div class="mx-3">
          <el-input type="text"
                    clearable class="input-sm"
                    placeholder="Filter results . . ."
                    v-model="query"
                    aria-controls="datatables"
                    style="width:250px;"
          />
        </div>
        <a @click.prevent="handleShowPreferenceModal"
            href="#"
        >
          <img style="width: 26px; height: 26px;"
            src="static/img/gear-icon.png"
            title="Configure Columns Displayed"
            class="mx-4"
          />
        </a>
      </div>
    </div>
    <el-table class="table-striped wrap-whole-words w-100"
              id="custom-table"
              ref="customTable"
              row-key="ptracTaskId"
              :key="tableKey"
              :data="sortedTableData"
              border
              :cell-class-name="cellClassName"
              :header-cell-class-name="headerSortClass"
              @select-all="handleSelectAll"
              @select="handleSelect"
              :select-on-indeterminate="true"
              @row-click="handleRowClick"
              max-height="430"
              style="width: 100%">
      <el-table-column
        v-if="!isSimulation"
        align="center"
        type="selection"
        :reserve-selection="true"
        width="55">
      </el-table-column>
      <el-table-column width="35px" align="center">
        <template #header>
          <routing-tooltip />
        </template>
        <template v-slot="props">
          <routing-tooltip :row="props.row" />
        </template>
      </el-table-column>
      <el-table-column v-for="column in filteredTableColumns"
                       :min-width="column.minWidth"
                       :max-width="column.maxWidth"
                       :label="column.label"
                       :key="column.label">
        <template v-slot="props">
          <component :is="column.component" :data="lodash.get(props.row, column.prop)" />
        </template>
      </el-table-column>
      <el-table-column
        v-if="isSimulation"
        label="Actual"
        prop="actualInspector"
        :min-width="150">
        <template v-slot="props">
          <div v-if="!props.row.inspections && props.row.actualInspector">
            {{ props.row.actualInspector.name }}
          </div>
        </template>
      </el-table-column>
    </el-table>
    <div class="pagination-info">
      <span class="category">Selected {{ selectedQueriedInspections.length }}/{{ queriedInspections.length }} filtered
        inspections. ({{ tableData.length }} inspections total.)</span>
    </div>
    <div v-if="!isSimulation" class="d-flex justify-content-end">
      <save-assignments-button :assignment-ids-to-save="selectedQueriedInspectionIds" />
    </div>
  </div>
</template>

<script>
import Constants from '@/utilities/constants'
import {
  assembleTableColumnSortProps,
  filterAndSortTableColumns,
  getTableHeight,
  sortNested,
  tableHeaderSortCssClass
} from '@/utilities/utils'
import {mapGetters} from 'vuex'
import {sync} from 'vuex-pathify'
import {Input} from 'element-ui'
import StringColumn from '@/components/UIComponents/TableColumns/StringColumn.vue'
import ProjectNameColumn from '@/components/UIComponents/TableColumns/ProjectNameColumn.vue'
import BooleanColumn from '@/components/UIComponents/TableColumns/BooleanColumn.vue'
import lodash, {debounce} from 'lodash'
import SaveAssignmentsButton from '@/components/Dashboard/Views/InspectorRouting/SaveAssignmentsButton.vue'
import BulkReassign from '@/components/Dashboard/Views/InspectorRouting/BulkReassign.vue'
import RoutingTooltip from '@/components/Dashboard/Views/InspectorRouting/RoutingTooltip.vue'

const PROJECT_NAME = "Project Name"

export default {
  props: ['tablesCached'],
  components: {
    RoutingTooltip,
    BulkReassign,
    SaveAssignmentsButton,
    ElInput: Input,
    StringColumn,
    ProjectNameColumn
  },
  computed: {
    isSimulation: sync('inspectorRouting/solverParams@isSimulation'),
    debouncedQuery: sync('inspectorRouting/query'),
    selectedInspectionIds: sync('inspectorRouting/selectedInspectionIds'),
    ...mapGetters({
      tableData: 'inspectorRouting/results',
      user: 'security/user',
    }),

    // Hacky fix for reactivity of selectedInspectionIds :Set (Vue2) using changeTracker
    selectedInspectionIdsReactive() {
      return this.selectedInspectionIdsChangeTracker && this.selectedInspectionIds
    },
    sortedTableData() {
      return this.queriedInspections.sort(sortNested(this.sortedProps))
    },
    filteredTableColumns() {
      return filterAndSortTableColumns(this.tableColumns, this.user, Constants.USER_PREFERENCE_TYPES.INSPECTIONS_ROUTING)
    },
    sortedProps() {
      return assembleTableColumnSortProps(this.filteredTableColumns)
    },
    queriedInspections() {
      if (!this.debouncedQuery) {
        return this.tableData
      }
      return this.tableData.filter(row => this.queryMatchInSelectedColumns(row))
    },
    selectedQueriedInspections() {
      return this.queriedInspections.filter(row => this.selectedInspectionIdsReactive.has(row.ptracTaskId))
    },
    selectedQueriedInspectionIds() {
      return this.selectedQueriedInspections.map(inspection => inspection.ptracTaskId)
    }
  },
  data() {
    return {
      lodash,
      constants: Constants,
      query: "",
      tableKey: 0,
      selectedInspectionIdsChangeTracker: 1,
      tableColumns: [
        {
          label: 'Inspection Type',
          prop: 'inspectionType',
          sortProp: 'inspectionType',
          minWidth: 300,
          component: StringColumn
        },
        {
          label: 'Reinspection',
          minWidth: 150,
          maxWidth: 150,
          prop: 'reinspection',
          sortProp: 'reinspection',
          component: BooleanColumn
        },
        {
          label: 'Assigned Inspector',
          prop: 'assignedInspector.name',
          sortProp: 'assignedInspector.name',
          minWidth: 210,
          maxWidth: 210,
          component: StringColumn
        },
        {
          label: 'City',
          prop: 'project.address.city',
          sortProp: 'project.address.city',
          minWidth: 150,
          component: StringColumn
        },
        {
          label: 'Community',
          prop: 'job.community.name',
          sortProp: 'job.community.name',
          minWidth: 160,
          component: StringColumn
        },
        {
          label: 'Task Priority',
          prop: 'ptracTask.priority',
          sortProp: 'ptracTask.priority',
          minWidth: 150,
          maxWidth: 150,
          component: StringColumn
        },
        {
          label: 'Task Template',
          prop: 'ptracTask.templateName',
          sortProp: 'ptracTask.templateName',
          minWidth: 350,
          component: StringColumn
        },
        {
          label: PROJECT_NAME,
          prop: 'project',
          sortProp: 'project.name',
          minWidth: 300,
          component: ProjectNameColumn
        },
        {
          label: 'Project Type',
          prop: 'project.projectType',
          sortProp: 'project.projectType',
          minWidth: 180,
          component: StringColumn
        },
        {
          label: 'Street Name',
          prop: 'project.address.streetName',
          sortProp: 'project.address.streetName',
          minWidth: 180,
          component: StringColumn
        },
        {
          label: 'Street #',
          prop: 'project.address.streetNumber',
          sortProp: 'project.address.streetNumber',
          minWidth: 105,
          maxWidth: 105,
          component: StringColumn
        },
        {
          label: 'Coordinates',
          prop: 'inspectionCoordinates',
          sortProp: 'inspectionCoordinates',
          minWidth: 180,
          component: StringColumn
        },
        {
          label: 'Suite #',
          prop: 'project.address.suiteNumber',
          sortProp: 'project.address.suiteNumber',
          minWidth: 90,
          maxWidth: 90,
          component: StringColumn
        },
        {
          label: 'Floor #',
          prop: 'project.address.floorNumber',
          sortProp: 'project.address.floorNumber',
          minWidth: 100,
          maxWidth: 100,
          component: StringColumn
        },
        {
          label: 'Lot #',
          prop: 'project.address.lotNumber',
          sortProp: 'project.address.lotNumber',
          minWidth: 75,
          maxWidth: 75,
          component: StringColumn
        },
        {
          label: 'Job Priority',
          prop: 'job.priority',
          sortProp: 'job.priority',
          minWidth: 140,
          maxWidth: 140,
          component: StringColumn
        },
        {
          label: 'Job Name',
          prop: 'job.name',
          sortProp: 'job.name',
          minWidth: 300,
          component: StringColumn
        },
        {
          label: 'Building #',
          prop: 'project.address.buildingNumber',
          sortProp: 'project.address.buildingNumber',
          minWidth: 120,
          maxWidth: 120,
          component: StringColumn
        },
        {
          label: 'Builder Name',
          prop: 'job.builderName',
          sortProp: 'job.builderName',
          minWidth: 180,
          component: StringColumn
        },
        {
          label: 'Client Name',
          prop: 'job.clientName',
          sortProp: 'job.clientName',
          minWidth: 180,
          component: StringColumn
        },

      ]
    }
  },
  methods: {
    tableHeight(buffer) {
      return getTableHeight(buffer)
    },
    headerSortClass({columnIndex}) {
      // styling for tooltip header cell
      if (this.isSimulation && columnIndex === 0 || !this.isSimulation && columnIndex === 1) {
        return 'tooltip-cell p-2'
      }

      return tableHeaderSortCssClass(this.filteredTableColumns, columnIndex, this.isSimulation ? 1 : 2)
    },
    cellClassName({row, column, rowIndex, columnIndex}) {
      // tooltip column - index changes depending on if selection column is included
      if ((this.isSimulation && columnIndex === 0) || (!this.isSimulation && columnIndex === 1)) {
        if (row.manuallyReassigned) {
          return 'tooltip-cell manually-reassigned'
        }
        if (row.unavailableManuallyAssignedInspector) {
          return 'tooltip-cell unavailable-manually-assigned-inspector'
        }
        if (row.manuallyAssignedInspector) {
          return 'tooltip-cell manually-assigned-inspector'
        }
        if (row.reinspection) {
          return 'tooltip-cell reinspection'
        }
        if (this.isSimulation) {
          return 'tooltip-cell'
        }

        return ''
      }

      if (column.property === 'actualInspector') {
        if (row.actualInspector.id === row.assignedInspector.id) {
          return 'assignment-match'
        } else {
          return 'assignment-non-match'
        }
      }
    },
    queryMatchInSelectedColumns(row) {
      for (const column of this.filteredTableColumns) {
        let data = row
        // ProjectName column prop is entireProject object - need to extract name manually for filter
        if (column.label === PROJECT_NAME) {
          data = data['project']['name']
        } else {
          const nestedProps = column.prop.split(".")
          for (const prop of nestedProps) {
            data = data[prop]
          }
        }

        if (data && data.toString().toLowerCase().includes(this.debouncedQuery.toLowerCase())) {
          return true
        }
      }
      return false
    },
    handleSelect(selection, row) {
      if (selection.includes(row)) {
        this.selectedInspectionIds.add(row.ptracTaskId)
        this.selectedInspectionIdsChangeTracker++
      } else {
        this.selectedInspectionIds.delete(row.ptracTaskId)
        this.selectedInspectionIdsChangeTracker++
      }
    },
    handleSelectAll(selection) {
      // select all button only controls currently queried inspections
        for (const inspection of this.queriedInspections) {
          if (selection.includes(inspection)) {
            this.selectedInspectionIds.add(inspection.ptracTaskId)
            this.selectedInspectionIdsChangeTracker++
          } else {
            this.selectedInspectionIds.delete(inspection.ptracTaskId)
            this.selectedInspectionIdsChangeTracker++
          }
      }
    },
    handleShowPreferenceModal() {
      const payload = {
        title: 'Inspector Routing',
        preferenceType: Constants.USER_PREFERENCE_TYPES.INSPECTIONS_ROUTING
      }
      this.$eventHub.$emit('show-modal-1', 'Search and Sort Preferences', payload, '900px')
    },
    handleRowClick(row){
      if (!this.isSimulation) {
        this.$eventHub.$emit('show-modal-1', "Change Assignment", row, '800px')
      }
    },
    toggleSelectedRowsFromSelectedInspections() {
      for (const row of this.$refs.customTable.data) {
        if (this.selectedInspectionIds.has(row.ptracTaskId)) {
          this.$refs.customTable.toggleRowSelection(row, true)
        } else {
          this.$refs.customTable.toggleRowSelection(row, false)
        }
      }
    },
    refreshTableAfterUserPreferencesSaved() {
      // change key on table to force vue to rerender it with new column preferences
      this.tableKey++
      this.$refs.customTable.doLayout()
      setTimeout(this.toggleSelectedRowsFromSelectedInspections, 100)
    }
  },
  created() {
    this.updateDebouncedQuery = debounce(() => {
      this.debouncedQuery = _.clone(this.query)
    }, 300)
  },
  mounted() {
    this.$eventHub.$on('assignmentChanged', (changedRow) => {
      // Only handle event if emitted with a 'row' payload (from ChangeAssignmentModal) not from BulkReassign (already selected)
      if (changedRow) {
        this.selectedInspectionIdsChangeTracker++
        this.$refs.customTable.toggleRowSelection(changedRow, true)
      }
    })

    this.$eventHub.$on('userPreferencesSaved', this.refreshTableAfterUserPreferencesSaved)
  },
  activated() {
    this.selectedInspectionIdsChangeTracker++
    setTimeout(this.toggleSelectedRowsFromSelectedInspections, 100)

  },
  beforeDestroy() {
    this.$eventHub.$off('assignmentChanged')
    this.$eventHub.$off('userPreferencesSaved')
  },
  watch: {
    query() {
      this.updateDebouncedQuery()
    },
    debouncedQuery: {
      handler: function() {
        this.tableKey++
        setTimeout(this.toggleSelectedRowsFromSelectedInspections, 100)
      }
    }
  }
}
</script>
